import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getExpensesInvoiceById, updateExpensesInvoice, updateExpensesInvoiceStatus } from '../../firebase/database';
import { generateExpensesInvoicePDF, downloadPDF, openPDFInNewTab, buildExpensesInvoiceFilename } from '../../services/invoicePdfService';
import { ExpensesInvoice, InvoiceItem } from '../../types';
import { roundMoney, mulMoney, addMoney, roundTo } from '../../utils/number';
import toast from 'react-hot-toast';
import LoadingOverlay from '../LoadingOverlay';

const EditExpensesInvoice: React.FC = () => {
  const navigate = useNavigate();
  const { invoiceId } = useParams();
  const { currentUser, currentMode } = useAuth();

  const portOfDestinationRef = useRef<HTMLInputElement>(null);
  const blAwbNumberRef = useRef<HTMLInputElement>(null);
  const currencyRef = useRef<HTMLSelectElement>(null);
  const exchangeRateRef = useRef<HTMLInputElement>(null);
  const itemDescriptionRefs = useRef<(HTMLInputElement | null)[]>([]);
  const itemQuantityRefs = useRef<(HTMLInputElement | null)[]>([]);
  const itemRateRefs = useRef<(HTMLInputElement | null)[]>([]);

  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState<ExpensesInvoice | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [error, setError] = useState('');
  const [showValidation, setShowValidation] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('Loading...');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        if (!invoiceId) return;
        const inv = await getExpensesInvoiceById(invoiceId);
        if (!inv) {
          setError('Expenses invoice not found');
          setInvoice(null);
          return;
        }
        const invExp = inv as unknown as ExpensesInvoice;
        const initialized: ExpensesInvoice = {
          ...invExp,
          awbNumber: invExp.awbNumber ?? '',
          portOfDestination: invExp.portOfDestination ?? '',
          blNumber: invExp.blNumber ?? ''
        };
        setInvoice(initialized);
      } catch (e) {
        console.error('Error loading expenses invoice:', e);
        setError('Failed to load expenses invoice');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [invoiceId]);

  const updateField = (field: keyof ExpensesInvoice, value: any) => {
    if (!invoice) return;
    const safeValue = typeof value === 'string' ? value : value ?? '';
    setInvoice({ ...invoice, [field]: safeValue });
  };

  const updateItem = (id: string, field: keyof InvoiceItem, value: any) => {
    if (!invoice) return;
    const items = invoice.items.map(it => {
      if (it.id !== id) return it;
      const updated = { ...it, [field]: value };
      if (field === 'rate' || field === 'quantity') {
        const newRate = field === 'rate' ? roundMoney(value) : it.rate;
        const newQty = field === 'quantity' ? value : it.quantity;
        updated.rate = newRate;
        updated.amount = mulMoney(newQty, newRate);
      }
      return updated;
    });
    setInvoice({ ...invoice, items });
  };

  const addItem = () => {
    if (!invoice) return;
    const newItem: InvoiceItem = {
      id: Date.now().toString(),
      description: '',
      quantity: 1,
      rate: 0,
      amount: 0,
      currency: invoice.currency
    };
    setInvoice({ ...invoice, items: [...invoice.items, newItem] });
  };

  const removeItem = (id: string) => {
    if (!invoice || invoice.items.length <= 1) return;
    setInvoice({ ...invoice, items: invoice.items.filter(item => item.id !== id) });
  };

  const subtotalPKR = useMemo(() => {
    if (!invoice) return 0;
    const total = invoice.items.reduce((sum, it) => {
      const itemTotal = it.currency !== 'PKR' ? mulMoney(it.amount, invoice.exchangeRate) : it.amount;
      return addMoney(sum, itemTotal);
    }, 0);
    return roundMoney(total);
  }, [invoice]);

  const totalPKR = useMemo(() => subtotalPKR, [subtotalPKR]);

  // Validate draft form - only requires client and logistics company
  const validateDraftForm = (): boolean => {
    if (!invoice) return false;

    const errors: Record<string, string> = {};
    
    // Only client and logistics company selection are required for draft
    if (!invoice.clientId) {
      errors['clientId'] = 'Required';
    }
    
    if (!invoice.logisticsCompany) {
      errors['logisticsCompany'] = 'Required';
    }
    
    if (!invoice.clientId || !invoice.logisticsCompany) {
      toast.error('Both Company and Logistics Company selection are required to save draft', { position: 'bottom-right' });
    }
    
    setFieldErrors(errors);
    
    return Object.keys(errors).length === 0;
  };

  const validateForm = (): boolean => {
    if (!invoice) return false;
    const errors: Record<string, string> = {};
    if (!invoice.portOfDestination) errors['portOfDestination'] = 'Required';
    if (!invoice.blNumber && !invoice.awbNumber) errors['blAwbNumber'] = 'Required';
    if (!invoice.currency) errors['currency'] = 'Required';
    if (!invoice.exchangeRate || invoice.exchangeRate <= 0) errors['exchangeRate'] = 'Exchange rate must be greater than 0';
    if (!invoice.invoiceDescription || !invoice.invoiceDescription.trim()) errors['invoiceDescription'] = 'Required';
    invoice.items.forEach((item, index) => {
      if (!item.description) errors[`item_${index}_description`] = 'Required';
      if (item.quantity <= 0) errors[`item_${index}_quantity`] = 'Must be > 0';
      if (item.rate <= 0) errors[`item_${index}_rate`] = 'Must be > 0';
    });
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildExpensesUpdateData = (status: ExpensesInvoice['status']) => {
    if (!invoice) {
      throw new Error('Expenses invoice not loaded');
    }

    return {
      invoiceNumber: invoice.invoiceNumber,
      clientId: invoice.clientId,
      clientName: invoice.clientName,
      date: invoice.date,
      logisticsCompany: invoice.logisticsCompany,
      logoUrl: invoice.logoUrl,
      awbNumber: invoice.awbNumber,
      portOfDestination: invoice.portOfDestination,
      blNumber: invoice.blNumber,
      items: invoice.items,
      subtotal: subtotalPKR,
      total: totalPKR,
      currency: invoice.currency,
      exchangeRate: invoice.exchangeRate,
      invoiceDescription: invoice.invoiceDescription || '',
      status,
      createdBy: invoice.createdBy
    };
  };

  const handleSave = async () => {
    if (!invoiceId || !invoice) return;
    setShowValidation(true);
    if (!validateForm()) {
      setError('Please fill all required fields to save invoice.');
      return;
    }
    setError('');
    setIsSaving(true);
    setIsLoading(true);
    setLoadingMessage('Saving expenses invoice...');
    try {
      // Always set status to 'pending' when saving and resubmitting
      const updateData = buildExpensesUpdateData('pending');

      await updateExpensesInvoice(invoiceId, updateData);
      toast.success('Expenses invoice updated and sent for approval');
      navigate('/expenses-dashboard');
    } catch (e) {
      console.error('Save error:', e);
      setError(`Failed to save expenses invoice: ${e instanceof Error ? e.message : 'Unknown error'}`);
    } finally {
      setIsSaving(false);
      setIsLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!invoiceId || !invoice) return;
    setShowValidation(true);
    if (!validateDraftForm()) {
      setError('Please select both Company and Logistics Company to save draft.');
      return;
    }
    setError('');

    setIsSavingDraft(true);
    setIsLoading(true);
    setLoadingMessage('Saving draft...');
    try {
      const updateData = buildExpensesUpdateData('draft');

      await updateExpensesInvoice(invoiceId, updateData);
      toast.success('Expenses invoice saved as draft');
    } catch (e) {
      console.error('Save draft error:', e);
      setError(`Failed to save draft: ${e instanceof Error ? e.message : 'Unknown error'}`);
    } finally {
      setIsSavingDraft(false);
      setIsLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!invoiceId || !invoice) return;
    setShowValidation(true);
    if (!validateForm()) {
      setError('Please fill all required fields to approve invoice.');
      return;
    }
    setError('');

    setIsApproving(true);
    setIsLoading(true);
    setLoadingMessage('Approving expenses invoice...');
    try {
      const updateData = buildExpensesUpdateData('approved');
      await updateExpensesInvoice(invoiceId, updateData);
      await updateExpensesInvoiceStatus(invoiceId, 'approved', currentUser?.id);
      toast.success('Expenses invoice updated and approved');
      navigate('/pending-expense-approvals');
    } catch (e) {
      console.error('Approve error:', e);
      toast.error('Failed to approve expenses invoice');
    } finally {
      setIsApproving(false);
      setIsLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!invoice) return;
    setShowValidation(true);
    if (!validateForm()) {
      setError('Please fill all required fields to preview PDF.');
      return;
    }
    setError('');
    setIsGeneratingPDF(true);
    setIsLoading(true);
    setLoadingMessage('Generating PDF preview...');
    try {
      const pdf = await generateExpensesInvoicePDF({ ...invoice, subtotal: subtotalPKR, total: totalPKR });
      openPDFInNewTab(pdf, 'Expenses Invoice PDF');
    } catch (e) {
      console.error('PDF error:', e);
      setError('Error generating PDF preview');
    } finally {
      setIsGeneratingPDF(false);
      setIsLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!invoice) return;
    setShowValidation(true);
    if (!validateForm()) {
      setError('Please fill all required fields to download PDF.');
      return;
    }
    setError('');

    setIsGeneratingPDF(true);
    setIsLoading(true);
    setLoadingMessage('Generating PDF download...');
    try {
      const enriched = { ...invoice, subtotal: subtotalPKR, total: totalPKR };
      const pdf = await generateExpensesInvoicePDF(enriched);
      downloadPDF(pdf, buildExpensesInvoiceFilename(enriched));
    } catch (e) {
      console.error('PDF download error:', e);
      setError('Error downloading PDF');
    } finally {
      setIsGeneratingPDF(false);
      setIsLoading(false);
    }
  };

  const isAuthorizerAmendingPending = currentUser?.role === 'authorizer' && invoice?.status === 'pending';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading expenses invoice...</p>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Expenses invoice not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 rounded-lg">
      <LoadingOverlay isVisible={isLoading} message={loadingMessage} />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Amend Expenses Invoice {invoice.invoiceNumber}</h1>
          <button onClick={() => navigate(-1)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md">Back</button>
        </div>

        {error && (
          <div className="mb-4 text-red-600 text-sm">{error}</div>
        )}

        <div className="bg-white shadow rounded-lg p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Port of Destination</label>
              <input
                ref={portOfDestinationRef}
                className="w-full px-3 py-2 border rounded-md"
                value={invoice.portOfDestination || ''}
                onChange={e => updateField('portOfDestination', e.target.value)}
              />
              {showValidation && fieldErrors['portOfDestination'] && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors['portOfDestination']}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">BL/AWB Number</label>
              <input
                ref={blAwbNumberRef}
                className="w-full px-3 py-2 border rounded-md"
                value={invoice.blNumber || invoice.awbNumber || ''}
                onChange={e => {
                  updateField('blNumber', e.target.value);
                  updateField('awbNumber', e.target.value);
                }}
              />
              {showValidation && fieldErrors['blAwbNumber'] && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors['blAwbNumber']}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
              <select
                ref={currencyRef}
                className="w-full px-3 py-2 border rounded-md"
                value={invoice.currency}
                onChange={e => updateField('currency', e.target.value)}
              >
                <option value="PKR">PKR</option>
                <option value="USD">USD</option>
                <option value="AED">AED</option>
              </select>
              {showValidation && fieldErrors['currency'] && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors['currency']}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Description *</label>
            <textarea
              value={invoice.invoiceDescription || ''}
              onChange={e => updateField('invoiceDescription', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Enter invoice description..."
              rows={3}
              required
            />
            {showValidation && fieldErrors['invoiceDescription'] && (
              <p className="mt-1 text-sm text-red-600">{fieldErrors['invoiceDescription']}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rate of Exchange</label>
              <input
                ref={exchangeRateRef}
                type="number"
                step="0.01"
                className="w-full px-3 py-2 border rounded-md"
                value={invoice.exchangeRate}
                onChange={e => updateField('exchangeRate', roundTo(parseFloat(e.target.value) || 1, 4))}
              />
              {showValidation && fieldErrors['exchangeRate'] && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors['exchangeRate']}</p>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-gray-900">Items</h3>
              <button
                type="button"
                onClick={addItem}
                className="px-3 py-1 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100"
              >
                + Add Item
              </button>
            </div>
            <div className="space-y-3">
              {invoice.items.map((it, index) => (
                <div key={it.id} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-12 sm:col-span-5">
                    <input
                      ref={el => itemDescriptionRefs.current[index] = el}
                      className="w-full px-3 py-2 border rounded-md"
                      value={it.description}
                      onChange={e => updateItem(it.id, 'description', e.target.value)}
                    />
                    {showValidation && fieldErrors[`item_${index}_description`] && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors[`item_${index}_description`]}</p>
                    )}
                  </div>
                  <div className="col-span-6 sm:col-span-2">
                    <input
                      ref={el => itemQuantityRefs.current[index] = el}
                      type="number"
                      className="w-full px-3 py-2 border rounded-md"
                      value={it.quantity}
                      onChange={e => updateItem(it.id, 'quantity', parseInt(e.target.value) || 0)}
                    />
                    {showValidation && fieldErrors[`item_${index}_quantity`] && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors[`item_${index}_quantity`]}</p>
                    )}
                  </div>
                  <div className="col-span-6 sm:col-span-2">
                    <input
                      ref={el => itemRateRefs.current[index] = el}
                      type="number"
                      step="0.01"
                      className="w-full px-3 py-2 border rounded-md"
                      value={it.rate}
                      onChange={e => updateItem(it.id, 'rate', parseFloat(e.target.value) || 0)}
                    />
                    {showValidation && fieldErrors[`item_${index}_rate`] && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors[`item_${index}_rate`]}</p>
                    )}
                  </div>
                  <select className="col-span-6 sm:col-span-2 px-3 py-2 border rounded-md" value={it.currency} onChange={e => updateItem(it.id, 'currency', e.target.value)}>
                    <option value="PKR">PKR</option>
                    <option value="USD">USD</option>
                    <option value="AED">AED</option>
                  </select>
                  <div className="col-span-6 sm:col-span-2">
                    <div className="space-y-1">
                      <div className="text-sm text-gray-500">Total Price</div>
                      <div className="text-sm font-medium">{it.currency} {roundMoney(it.amount).toFixed(2)}</div>
                      {it.currency !== 'PKR' && (
                        <div className="text-xs text-blue-600">
                          PKR: {roundMoney(mulMoney(it.amount, invoice.exchangeRate)).toFixed(2)}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-span-12 sm:col-span-1 flex sm:block justify-end">
                    {invoice.items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(it.id)}
                        className="text-red-600 hover:text-red-800 mt-2 sm:mt-6"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 p-3 rounded">
              <div className="flex justify-between text-sm"><span>Subtotal (PKR):</span><span>{subtotalPKR.toFixed(2)}</span></div>
              <div className="flex justify-between text-base font-bold border-t pt-2"><span>Total (PKR):</span><span>{totalPKR.toFixed(2)}</span></div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex gap-3">
              <button onClick={handlePreview} disabled={isGeneratingPDF} className="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 disabled:opacity-50">{isGeneratingPDF ? 'Generating...' : 'Preview PDF'}</button>
              <button onClick={handleDownloadPDF} disabled={isGeneratingPDF} className="px-4 py-2 text-sm font-medium text-green-600 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 disabled:opacity-50">{isGeneratingPDF ? 'Generating...' : 'Download PDF'}</button>
              {!isAuthorizerAmendingPending && (
                <>
                  <button onClick={handleSaveDraft} disabled={isSavingDraft} className="px-4 py-2 text-sm font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-md hover:bg-orange-100 disabled:opacity-50">{isSavingDraft ? 'Saving...' : 'Save as Draft'}</button>
                  <button onClick={handleSave} disabled={isSaving} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">{isSaving ? 'Saving...' : 'Save & Resubmit'}</button>
                </>
              )}
            </div>
            {currentUser?.role === 'authorizer' && currentMode !== 'inputter' && (
              <button onClick={handleApprove} disabled={isApproving} className="px-6 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50">{isApproving ? 'Approving...' : 'Approve'}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditExpensesInvoice;


